import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import kvsLogo from '../../assets/kvsLogo.svg';
// header shape moved inline for better responsiveness
import { authService } from '../../services/authService';
import { apiPost } from '../../shared/services/apiClient';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { createPortal } from 'react-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonIcon from '@mui/icons-material/Person';
import { CSP_NONCE } from '../../shared/services/cspNonce';

const headerStyles = `
  @media (min-width: 1130px) {
    #desktop-header {
      display: block !important;
    }
    #mobile-header {
      display: none !important;
    }
  }
  
  @media (max-width: 1129px) {
    #desktop-header {
      display: none !important;
    }
    #mobile-header {
      display: block !important;
    }
  }
`;

type HeaderProps = {
  loggedIn?: boolean;
};

export default function Header({ loggedIn }: HeaderProps) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  // Get user info from localStorage
  const userName = localStorage.getItem('username') || 'User';
  const userRole = localStorage.getItem('userRole') || '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target as Node);
      const clickedOnButton = dropdownButtonRef.current && dropdownButtonRef.current.contains(event.target as Node);
      if (!clickedInsideDropdown && !clickedOnButton) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check authentication status on mount and listen for storage changes
  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(localStorage.getItem('isLoggedIn') === 'true');
    };

    checkAuth();

    // Check every second for auth changes
    const interval = setInterval(checkAuth, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const handleLogout = async () => {
    try {
      // Call logout API endpoint
      await apiPost('/auth/logout', {});

      // Clear local storage
      authService.logout();

      // Update state
      setIsAuthenticated(false);
      setShowDropdown(false);

      // Redirect to landing page
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if API fails, clear local storage and redirect
      authService.logout();
      setIsAuthenticated(false);
      setShowDropdown(false);
      navigate('/');
    }
  };

  // Toggle dropdown and set the button element we measured from
  const toggleDropdown = (e: any) => {
    // store the clicked button so measurements always come from the actual trigger
    dropdownButtonRef.current = e.currentTarget as HTMLButtonElement;

    setShowDropdown(prev => {
      const next = !prev;
      if (next) {
        // measure on the next frame to ensure layout is stable
        requestAnimationFrame(calculateDropdownPosition);
      } else {
        setDropdownPos(null);
      }

      return next;
    });
  };

  // Recalculate dropdown position relative to the viewport
  const calculateDropdownPosition = () => {
    const btn = dropdownButtonRef.current;
    if (!btn) {
      setDropdownPos(null);
      return;
    }
    const rect = btn.getBoundingClientRect();
    const dropdownWidth = 224; // w-56 in px (14rem * 16px = 224)
    let left = rect.right - dropdownWidth;
    left = Math.max(8, left);
    left = Math.min(left, window.innerWidth - dropdownWidth - 8);
    const top = rect.bottom + window.scrollY + 8; // small margin
    setDropdownPos({ top, left });
  };

  useEffect(() => {
    if (showDropdown) {
      calculateDropdownPosition();
    }
  }, [showDropdown]);

  useEffect(() => {
    const onResizeOrScroll = () => showDropdown && calculateDropdownPosition();
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll);
    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll);
    };
  }, [showDropdown]);
  const portalStyle = dropdownPos
    ? { position: 'absolute', top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, width: '224px', zIndex: 9999 }
    : undefined;

  return (
    <>
    <style nonce={CSP_NONCE}>{headerStyles}</style>
    <div className="w-full relative z-50 m-0 p-0 leading-[0]" style={{ display: 'block' }}>
      {/* DESKTOP LAYOUT (>= 1130px) */}
      <div className="hidden" style={{ display: 'none' }} id="desktop-header">
        <div className="relative w-full h-[90px] sm:h-[100px] md:h-[120px] lg:h-[142px] block m-0 p-0" style={{ display: 'block' }}>
          {/* SVG Background (inline for responsiveness) */}
          <div className="absolute inset-0 w-full h-full">
            <svg
              viewBox="0 0 1920 142"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-full"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="paint0" x1="876" y1="15" x2="1920" y2="15" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#481789" />
                  <stop offset="1" stopColor="#6A1786" />
                </linearGradient>

                <linearGradient id="paint1" x1="1105" y1="86" x2="1920" y2="86" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#551787" />
                  <stop offset="1" stopColor="#6B1786" />
                </linearGradient>
              </defs>

              {/* TOP BAR */}
              <rect width="1920" height="30" fill="url(#paint0)" />

              {/* SHAPE */}
              <path
                d="M1176.12 116.392
                   L1145.37 56.6392
                   C1134.99 33.165 1126.59 30.3039 1113.87 30.0265
                   H1105.28
                   C1108.37 30.0232 1111.21 29.9686 1113.87 30.0265
                   H1920
                   V142
                   H1208.41
                   C1186.58 142 1180.73 126.936 1176.12 116.392
                   Z"
                fill="url(#paint1)"
              />
            </svg>
          </div>

          {/* Content Overlay */}
          <div className="relative w-full h-full flex flex-col overflow-hidden">
            {/* First Row - Government of India */}
            <div className="w-full h-[18px] sm:h-[20px] md:h-[25px] lg:h-[30px] flex items-center bg-transparent flex-shrink-0">
              <div className="w-full px-3 sm:px-4 md:px-6 lg:px-12 flex items-center justify-start h-full overflow-hidden">
                <span className="text-white text-[8px] sm:text-[9px] md:text-[10px] lg:text-xs font-bold tracking-wide truncate">भारत सरकार | Government of India</span>
              </div>
            </div>

            {/* Second Row - Main Header Content - Three distinct sections */}
            <div className="flex-1 w-full flex items-center min-h-0">
              <div className="relative w-full px-3 sm:px-4 md:px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-1.5 sm:gap-2 lg:gap-6 z-10 py-1 sm:py-1.5 md:py-2">
                
                {/* Section 1: Logo and Organization Info */}
                <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 z-10 flex-shrink-0 min-w-0 max-w-full lg:max-w-[50%]">
                    <img
                      src={kvsLogo}
                      alt="KVS Logo"
                      className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 lg:h-[70px] lg:w-[70px] xl:h-[76px] xl:w-[76px] object-contain flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <h2 className="text-[10px] sm:text-xs md:text-sm lg:text-base xl:text-lg font-bold text-gray-900 mb-0 leading-tight truncate">
                        केन्द्रीय विद्यालय संगठन
                      </h2>
                      <p className="text-[8px] sm:text-[9px] md:text-[10px] lg:text-xs text-gray-600 mb-0 font-semibold truncate">
                        Kendriya Vidyalaya Sangathan
                      </p>
                    <p className="hidden xl:block text-[9px] lg:text-[10px] mt-2 text-gray-500 font-normal leading-tight truncate">
                      An Autonomous Body Under Ministry of Education, Government of India
                    </p>
                  </div>
                </div>

                {/* Section 2 & 3: VIDYA SAMIKSHA KENDRA + Login (wraps together on overflow) */}
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 xl:gap-5 relative z-[100] w-full lg:w-auto lg:flex-1 lg:justify-end min-w-0">
                  {/* VIDYA SAMIKSHA KENDRA */}
                  <h1 className="text-[10px] sm:text-xs md:text-sm lg:text-base xl:text-lg 2xl:text-xl font-bold tracking-wide md:tracking-wider lg:tracking-widest uppercase text-white flex-shrink-0">
                    <span className="whitespace-nowrap">VIDYA SAMIKSHA KENDRA</span>
                  </h1>
                  
                  {/* Login Component */}
                  {isAuthenticated && (
                    <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5 flex-shrink-0">
                      <div className="h-4 sm:h-5 md:h-6 lg:h-8 w-px bg-white/30 hidden sm:block"></div>
                      <div className="relative">
                        <button
                          onClick={toggleDropdown}
                          className="flex items-center gap-0.5 sm:gap-1 md:gap-1.5 text-white hover:bg-white/10 rounded-lg px-1 sm:px-1.5 md:px-2 py-0.5 sm:py-1 md:py-1.5 transition-all"
                        >
                          <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 rounded-full bg-white/20 border-2 border-white flex items-center justify-center flex-shrink-0">
                            <PersonIcon sx={{ fontSize: 12 }} />
                          </div>
                          <span className="hidden sm:inline text-[9px] md:text-[10px] lg:text-xs font-medium max-w-[60px] md:max-w-[80px] truncate">{userName}</span>
                          <ArrowDropDownIcon sx={{ fontSize: 16 }} className={`transition-transform flex-shrink-0 ${showDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown Menu */}
                        {/* Dropdown will be rendered via portal so it overlays other content */}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE/TABLET LAYOUT (< 1130px) - Three Strip Layout */}
      <div className="block" id="mobile-header">
        <div className="w-full flex flex-col">
          {/* Strip 1: Government of India */}
          <div className="w-full h-[24px] sm:h-[28px] md:h-[32px] flex items-center flex-shrink-0" style={{ background: 'linear-gradient(90deg, #551787 0%, #6B1786 100%)' }}>
            <div className="w-full px-3 sm:px-4 md:px-6 flex items-center justify-start h-full">
              <span className="text-white text-[8px] sm:text-[9px] md:text-[10px] font-bold tracking-wide truncate">भारत सरकार | Government of India</span>
            </div>
          </div>

          {/* Strip 2: Logo and Organization Info */}
          <div className="w-full bg-white flex items-center flex-shrink-0 py-2 sm:py-2.5 md:py-3">
            <div className="w-full px-3 sm:px-4 md:px-6 flex items-start gap-2 sm:gap-2.5 md:gap-3">
              <img
                src={kvsLogo}
                alt="KVS Logo"
                className="h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12 object-contain flex-shrink-0 mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <h2 className="text-[10px] sm:text-xs md:text-sm font-bold text-gray-900 mb-0.5 leading-tight">
                  केन्द्रीय विद्यालय संगठन
                </h2>
                <p className="text-[8px] sm:text-[9px] md:text-[10px] text-gray-600 mb-2 font-semibold leading-tight">
                  Kendriya Vidyalaya Sangathan
                </p>
                <p className="text-[7px] sm:text-[8px] md:text-[9px] text-gray-500 font-normal leading-tight">
                  Ministry of Education, Government of India
                </p>
              </div>
            </div>
          </div>

          {/* Strip 3: VIDYA SAMIKSHA KENDRA + Login */}
          <div className="w-full flex items-center justify-between flex-shrink-0 py-2 sm:py-2.5 md:py-3" style={{ background: 'linear-gradient(90deg, #551787 0%, #6B1786 100%)' }}>
            <div className="w-full px-3 sm:px-4 md:px-6 flex items-center justify-between gap-2 sm:gap-3">
              <h1 className="text-[9px] sm:text-[10px] md:text-xs font-bold tracking-wide uppercase text-white flex-shrink-0 whitespace-nowrap">
                VIDYA SAMIKSHA KENDRA
              </h1>
              
              {/* Login Component */}
              {isAuthenticated && (
                <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                  <div className="relative">
                    <button
                      onClick={toggleDropdown}
                      className="flex items-center gap-0.5 text-white hover:bg-white/10 rounded-lg px-1 sm:px-1.5 py-0.5 sm:py-1 transition-all"
                    >
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white/20 border-2 border-white flex items-center justify-center flex-shrink-0">
                        <PersonIcon sx={{ fontSize: 12 }} />
                      </div>
                      <span className="hidden sm:inline text-[8px] md:text-[9px] font-medium max-w-[50px] truncate">{userName}</span>
                      <ArrowDropDownIcon sx={{ fontSize: 14 }} className={`transition-transform flex-shrink-0 ${showDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {/* Dropdown will be rendered via portal so it overlays other content */}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Optional status indicator */}
      {typeof loggedIn !== 'undefined' && (
        <div className="absolute right-4 top-2 z-30 text-white text-xs">
          {loggedIn ? 'Signed in' : 'Guest'}
        </div>
      )}
    </div>
    
    {showDropdown && dropdownPos && createPortal(
      <div
        className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200"
        style={portalStyle as React.CSSProperties}
        ref={dropdownRef}
      >
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <p className="text-sm font-semibold text-gray-900">{userName}</p>
          <p className="text-xs text-gray-500">{userRole}</p>
        </div>
        <div className="py-1">
          <button
            onClick={() => { setShowDropdown(false); navigate('/change-password'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
          >
            <LockResetIcon sx={{ fontSize: 20 }} />
            <span>Change Password</span>
          </button>
          <button
            onClick={() => { setShowDropdown(false); handleLogout(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogoutIcon sx={{ fontSize: 20 }} />
            <span>Logout</span>
          </button>
        </div>
      </div>
      , document.body)
    }
    </>
  );
}
